import { nonOptimistic } from '@rocketlane/state-db';
import { ProjectAPI } from 'api/project';
import { buildPhaseOptimisticPatch } from './payload';
import { getPhaseFromCollection, phaseCollection } from './phase-collection';
/**
 * Provenance for every push in this module — the WRITE layer. Reads live in
 * `fetchers.ts` (default `'fetch'`, silent). Same convention as
 * `collections/project/actions.ts`.
 */ const MUTATION_PUSH = {
    source: 'mutation'
};
/**
 * Optimistic phase field/date/status/private-toggle edit (the primitive).
 * Requires the phase already in the collection — needs the original to merge
 * the whitelisted patch against and to roll back to. `projectId` is only
 * needed when the cached phase can't resolve its own (see
 * `phase-collection.ts:resolveProjectId`) — pass it when known to avoid a
 * throw on a phase that was never stamped.
 */ export const updatePhaseOptimistic = (phaseId, payload, projectId)=>{
    const original = getPhaseFromCollection(phaseId);
    if (!original) {
        throw new Error(`updatePhaseOptimistic: phase ${phaseId} not in collection`);
    }
    return phaseCollection.update(phaseId, buildPhaseOptimisticPatch(original, payload), projectId != null ? {
        metadata: {
            projectId
        }
    } : undefined);
};
/**
 * Awaitable variant resolving to the server-confirmed phase (or rejecting,
 * after rollback, on server error) — for callers needing the entity
 * synchronously (grid renderers, modals). Falls back to a direct write when
 * the phase isn't cached yet (no overlay base to patch).
 */ export const updatePhaseRecord = async (phaseId, payload, projectId)=>{
    const original = getPhaseFromCollection(phaseId);
    if (!original) {
        if (projectId == null) {
            throw new Error(`updatePhaseRecord: phase ${phaseId} not cached and no projectId given`);
        }
        const updated = await ProjectAPI.updateProjectPhase(projectId, phaseId, payload);
        phaseCollection.push([
            {
                ...updated,
                projectId
            }
        ], MUTATION_PUSH);
        return updated;
    }
    const tx = updatePhaseOptimistic(phaseId, payload, projectId);
    await tx.isPersisted; // rejects on server error — overlay already rolled back
    return getPhaseFromCollection(phaseId);
};
/**
 * Non-optimistic: the server assigns the new phase's position/order and any
 * inherited defaults — there is no useful client-side draft to show. Mirrors
 * `createProjectRecord`'s reasoning.
 */ export const createPhaseRecord = nonOptimistic({
    name: 'phase.create',
    because: 'no onInsert handler — the server derives ordering and defaults for a ' + 'new phase; there is no client-side draft worth showing before it responds',
    run: async (projectId, payload)=>{
        const phase = await ProjectAPI.createProjectPhase(projectId, payload);
        phaseCollection.push([
            {
                ...phase,
                projectId
            }
        ], MUTATION_PUSH);
        return phase;
    }
});
/**
 * Delete a phase. Optimistic when cached — unlike Project (which has
 * dependent-entity ambiguity blocking an optimistic delete), a phase delete
 * has no such concern.
 */ export const deletePhaseRecord = async (projectId, phaseId)=>{
    if (getPhaseFromCollection(phaseId) === undefined) {
        // Never loaded here — nothing to hide, and no original to restore.
        await ProjectAPI.deleteProjectPhase(projectId, phaseId);
        phaseCollection.externalDelete(phaseId, {
            source: 'mutation'
        });
        return;
    }
    await phaseCollection.delete(phaseId).isPersisted;
};
/**
 * Reorder a project's phases. The API takes the whole ordered id list and the
 * order lives on `project.projectPhases` array order, not on the phase bean
 * itself — so this is a thin API wrapper, not a `phaseCollection` mutation.
 * The optimistic array-order patch stays where it is today
 * (`collections/plan-board/actions.ts:reorderProjectPhasesOptimistic`, which
 * patches `projectCollection`) until the read side (Wave 2) derives
 * `project.projectPhases` order from this collection instead of storing it
 * twice.
 */ export const reorderPhasesRecord = async (projectId, phaseOrder)=>ProjectAPI.reorderPhases(projectId, {
        phaseOrder
    });
/**
 * Mirror an `entityOrder` change that a caller has already applied to
 * `project.projectPhases[]` onto the phase beans here.
 *
 * `entityOrder` has two homes now. The gantt and the plan build their rows from
 * the project's copy; `useProjectPhaseByProjectId` — and through it
 * `useTransformTabularData`, so the rearrange tree — reads the bean. A writer
 * that patches only the project leaves the bean on its pre-write order until
 * the next fetch, and the tree keeps rendering a phase the task has already
 * left, or omits one it has just joined.
 *
 * Re-reads each bean instead of pushing the project's copy of it: the bean also
 * carries the aggregates `phase-progress-reconciler` owns, which the project's
 * copy can be staler about, so a whole-copy push would silently undo them.
 *
 * `source` is the caller's, not ours. Pass `'local'` from anything that can roll
 * back — a broadcast guess cannot be retracted on a peer (see `patchEntityOrder`
 * in `collections/tasks/mutations.ts`) — and `'mutation'` only once the change
 * is settled. Returns the undo, so a companion patch can hand it straight back.
 */ export const mirrorPhaseEntityOrder = (changedPhases, source)=>{
    const before = [];
    const after = [];
    changedPhases.forEach(({ projectPhaseId, entityOrder })=>{
        const bean = getPhaseFromCollection(projectPhaseId);
        // Not hydrated here yet — the next fetch mirrors it in full, and a partial
        // bean built from an order alone would be worse than none.
        if (bean === undefined) {
            return;
        }
        before.push(bean);
        after.push({
            ...bean,
            entityOrder
        });
    });
    if (after.length === 0) {
        return ()=>undefined;
    }
    phaseCollection.push(after, {
        source
    });
    return ()=>{
        phaseCollection.push(before, {
            source
        });
    };
};
